import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Dumbbell, Timer, TrendingUp, Weight, Sparkles, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

function Metric({ icon: Icon, label, value, sub, accent = "#FF3B30", testid }) {
  return (
    <div className="p-6 bg-[#121212] border border-white/10 hover:border-white/25 transition-transform duration-150 hover:-translate-y-1" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-white/50">{label}</span>
        <Icon size={18} style={{ color: accent }} />
      </div>
      <div className="font-display font-black text-4xl tabular-nums tracking-tighter">{value}</div>
      {sub && <div className="text-sm text-white/40 mt-1">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [weights, setWeights] = useState([]);
  const [workouts, setWorkouts] = useState([]);

  const load = async () => {
    const [s, w, wk] = await Promise.all([api.get("/stats"), api.get("/weights"), api.get("/workouts")]);
    setStats(s.data);
    setWeights(w.data);
    setWorkouts(wk.data);
  };
  useEffect(() => { load(); }, []);

  const weightData = weights.map((w) => ({ date: w.date?.slice(5), weight: w.weight }));
  const volumeData = [...workouts].reverse().slice(-8).map((w) => ({
    date: w.date?.slice(5),
    volume: (w.exercises || []).reduce((a, e) => a + e.sets * e.reps * e.weight, 0),
  }));

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#FF3B30] mb-2">Dashboard</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tighter leading-none">Welcome, {user?.name?.split(" ")[0]}</h1>
        </div>
        <Link to="/workouts" data-testid="dashboard-log-workout" className="bg-[#FF3B30] hover:bg-[#FF453A] text-white font-bold uppercase tracking-wide text-sm px-5 py-3 transition-transform duration-150 hover:-translate-y-0.5">
          Log Workout
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Metric icon={Dumbbell} label="Workouts" value={stats?.total_workouts ?? 0} sub="sessions logged" testid="metric-workouts" />
        <Metric icon={Timer} label="Total Time" value={stats?.total_minutes ?? 0} sub="minutes trained" accent="#FF9500" testid="metric-time" />
        <Metric icon={TrendingUp} label="Volume" value={(stats?.total_volume ?? 0).toLocaleString()} sub="kg lifted" accent="#34C759" testid="metric-volume" />
        <Metric icon={Weight} label="Body Weight" value={stats?.latest_weight ? `${stats.latest_weight}` : "—"} sub={stats?.weight_change ? `${stats.weight_change > 0 ? "+" : ""}${stats.weight_change} change` : "no data"} accent="#007AFF" testid="metric-weight" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-8">
        <div className="p-6 bg-[#121212] border border-white/10">
          <h3 className="font-display font-bold text-xl uppercase tracking-tight mb-6">Body Weight Trend</h3>
          {weightData.length > 1 ? (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={weightData}>
                <CartesianGrid stroke="#ffffff10" vertical={false} />
                <XAxis dataKey="date" stroke="#71717A" fontSize={11} />
                <YAxis stroke="#71717A" fontSize={11} domain={["auto", "auto"]} />
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #ffffff20", borderRadius: 0, color: "#fff" }} />
                <Line type="monotone" dataKey="weight" stroke="#007AFF" strokeWidth={2.5} dot={{ r: 3, fill: "#007AFF" }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart to="/weight" label="Log your body weight to see trends" />
          )}
        </div>

        <div className="p-6 bg-[#121212] border border-white/10">
          <h3 className="font-display font-bold text-xl uppercase tracking-tight mb-6">Workout Volume</h3>
          {volumeData.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={volumeData}>
                <CartesianGrid stroke="#ffffff10" vertical={false} />
                <XAxis dataKey="date" stroke="#71717A" fontSize={11} />
                <YAxis stroke="#71717A" fontSize={11} />
                <Tooltip cursor={{ fill: "#ffffff08" }} contentStyle={{ background: "#0A0A0A", border: "1px solid #ffffff20", borderRadius: 0, color: "#fff" }} />
                <Bar dataKey="volume" fill="#FF3B30" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart to="/workouts" label="Log a workout with exercises to see volume" />
          )}
        </div>
      </div>

      {/* AI coach banner */}
      {!user?.has_coach && (
        <Link to="/plans" data-testid="dashboard-coach-banner" className="block p-6 border border-[#007AFF]/30 bg-[#007AFF]/5 hover:bg-[#007AFF]/10 transition-colors">
          <div className="flex items-center gap-4">
            <Sparkles className="text-[#007AFF]" size={28} />
            <div className="flex-1">
              <div className="font-display font-bold text-xl uppercase tracking-tight">Upgrade to Pro for your AI Coach</div>
              <div className="text-white/50 text-sm">Personalized training & nutrition guidance, 24/7 — $14.99/mo.</div>
            </div>
            <ArrowRight className="text-[#007AFF]" />
          </div>
        </Link>
      )}
    </div>
  );
}

function EmptyChart({ to, label }) {
  return (
    <Link to={to} className="h-[240px] flex flex-col items-center justify-center text-center border border-dashed border-white/10 text-white/40 hover:text-white/70 hover:border-white/25 transition-colors">
      <span className="text-sm">{label}</span>
    </Link>
  );
}
